import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';
import { submit } from 'redux-form';
import { Button, Chip, CircularProgress, IconButton, Link, Tab, Tabs, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { countWithoutPack, editPack, getPacks, PACK_COVER_URL, updatePack } from '../../actions/packs';
import { push } from '../../actions/utils';
import CustomCard from '../../components/CustomCard';
import ModalDialog from '../../components/ModalDialog';
import PackIcon from '../../components/PackIcon';
import { NO_PACK, PACK_TABS, packPath, tabFromQuery } from '../../packNav';
import { validatePack } from '../../validation';
import { PackEditionForm } from './PackForm';
import PackItemsTab from './PackItemsTab';
import { PACK_KINDS } from './packKinds';
import PackTemplatesTab from './PackTemplatesTab';

const NO_PACK_ROW = { objectId: NO_PACK, name: 'Sans pack', virtual: true };

// A pack's page: header, then one tab per item kind (the tab is in the URL: ?tab=…).
const PackPage = ({ params, location }) => {
  const dispatch = useDispatch();
  const packId = params.packId;
  const tab = tabFromQuery(location?.query);
  const packs = useSelector((state) => state.packs.packs);
  const editedPack = useSelector((state) => state.packs.pack);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'failed'
  const [noPackCounts, setNoPackCounts] = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    dispatch(getPacks())
      .then(() => setStatus('ready'))
      .catch((error) => {
        console.error(error);
        setStatus('failed');
      });
  }, [dispatch]);

  // « Sans pack » has no row in Parse: its counts are fetched apart, again whenever the packs reload
  useEffect(() => {
    if (packId !== NO_PACK) return;
    countWithoutPack()
      .then(setNoPackCounts)
      .catch((error) => console.error(error));
  }, [packId, packs]);

  if (status === 'loading') return <CircularProgress size={28} style={{ margin: 24 }} />;
  if (status === 'failed') return <Typography color="error">Impossible de charger le pack.</Typography>;

  const pack = packId === NO_PACK ? { ...NO_PACK_ROW, counts: noPackCounts } : packs.find((p) => p.objectId === packId);
  if (!pack) {
    return (
      <Typography style={{ margin: 24 }}>
        Pack introuvable. <Link component="button" onClick={() => push('/packs')}>Retour aux packs</Link>
      </Typography>
    );
  }

  const handleEdit = async () => {
    await dispatch(editPack(packId));
    setEditing(true);
  };

  const handleUpdate = async (values) => {
    validatePack(values);
    await dispatch(updatePack(editedPack, values));
    setEditing(false);
  };

  const title = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <IconButton size="small" onClick={() => push('/packs')} aria-label="Retour aux packs">
        <ArrowBackIcon />
      </IconButton>
      {pack.cover && <img src={PACK_COVER_URL + pack.cover} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} />}
      <PackIcon name={pack.icon} size={24} />
      <span>{pack.name}</span>
      {!pack.virtual && <Chip size="small" label={pack.published ? 'Publié' : 'Brouillon'} color={pack.published ? 'secondary' : 'default'} />}
      {pack.isBase && <Chip size="small" label="Pack de base" />}
    </div>
  );

  return (
    <>
      <CustomCard
        title={title}
        actionHeaderButtons={pack.virtual ? null : <Button onClick={handleEdit}>Modifier</Button>}
        content={
          // One wrapper: CustomCard gives each direct child div of its content a 85vh height.
          <div>
            <Tabs value={tab} onChange={(event, key) => browserHistory.replace(packPath(packId, key))} indicatorColor="primary" textColor="primary">
              {PACK_TABS.map(({ key, label, countKey }) => (
                <Tab key={key} value={key} label={`${label} (${pack.counts?.[countKey] ?? '…'})`} />
              ))}
            </Tabs>
            {tab === 'modeles' ? <PackTemplatesTab key={packId} packId={packId} /> : <PackItemsTab key={`${packId}-${tab}`} packId={packId} kind={PACK_KINDS[tab]} />}
          </div>
        }
        withActionButtons={false}
        fullScreen
      />
      <ModalDialog
        title={`Modifier ${pack.name}`}
        content={<PackEditionForm onSubmit={handleUpdate} />}
        isVisible={editing}
        onConfirm={() => dispatch(submit('packForm'))}
        onClose={() => setEditing(false)}
        labelConfirm="Enregistrer"
      />
    </>
  );
};

PackPage.propTypes = {
  params: PropTypes.object.isRequired,
  location: PropTypes.object,
};

export default PackPage;
